import type { SiteEvent } from '../api/public'

const TZ = 'Europe/Zurich'

export function formatCrtDateDots(iso: string, timeZone = TZ): string {
  const d = new Date(iso)
  const parts = new Intl.DateTimeFormat('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone,
  }).formatToParts(d)
  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? ''
  return `${day} · ${month} · ${year}`
}

export function formatCrtTodayDots(timeZone = TZ): string {
  return formatCrtDateDots(new Date().toISOString(), timeZone)
}

/** "30 · 04 · 26" short for card corner */
export function formatCrtDateCompact(iso: string, timeZone = TZ): string {
  const d = new Date(iso)
  const parts = new Intl.DateTimeFormat('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone,
  }).formatToParts(d)
  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? ''
  return `${day}·${month}·${year}`
}

export function relativeTransmissionLabel(startsAt: string): string {
  const start = new Date(startsAt).getTime()
  const now = Date.now()
  const diffMs = start - now
  if (diffMs < 0) {
    // Still show as running if started within the last 24 h
    const hoursAgo = Math.abs(diffMs) / 3600000
    return hoursAgo < 24 ? 'LÄUFT' : 'VERGANGEN'
  }
  const hours = Math.ceil(diffMs / 3600000)
  if (hours === 0) return 'HEUTE'
  if (hours < 34) return `IN ${hours} STUNDEN`
  const days = Math.ceil(diffMs / 86400000)
  if (days === 1) return 'IN 1 TAG'
  return `IN ${days} TAGEN`
}

export function formatCrtTimeVenue(ev: SiteEvent): string {
  const tz = ev.timezone || TZ
  const t = new Intl.DateTimeFormat('de-CH', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  }).format(new Date(ev.startsAt))
  const place = (ev.venueName || ev.locationLabel || 'TBA').toUpperCase()
  return `${t} — ${place}`
}

/** Split title into 2–4 lines for CRT display */
export function splitTitleForCrt(title: string, maxLines = 4): string[] {
  const words = title.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ['EVENT']
  const lines: string[] = []
  let cur = ''
  const maxChunk = 14
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w
    if (next.length > maxChunk && cur) {
      lines.push(cur.toUpperCase())
      cur = w
      if (lines.length >= maxLines) break
    } else {
      cur = next
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur.toUpperCase())
  return lines.slice(0, maxLines).map((l) => l.toUpperCase())
}

export function cardTopEyebrow(ev: SiteEvent): string {
  const t = ev.title.slice(0, 22).toUpperCase()
  return `> ${t}${ev.title.length > 22 ? '…' : ''}`
}
